import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { Sun, Loader2, Sparkles, Coffee, Calendar, Waves } from "lucide-react";

const MONTHS = [
  { key: "haziran", label: "Haziran", tema: "Tekrar Ayı", icon: Coffee },
  { key: "temmuz", label: "Temmuz", tema: "Temel Güçlendirme", icon: Sparkles },
  { key: "agustos", label: "Ağustos", tema: "Deneme + Yeni Nesil", icon: Calendar },
];

export default function YazKampiPage() {
  const [month, setMonth] = useState("haziran");
  const [minutes, setMinutes] = useState(60);
  const [vacation, setVacation] = useState(false);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (m = month, mins = minutes, vac = vacation) => {
    setLoading(true);
    try {
      const r = await api.get(`/coach/summer-plan?month=${m}&minutes=${mins}&vacation=${vac}`);
      setPlan(r.data);
    } catch {
      toast.error("Plan oluşturulamadı");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load("haziran", 60, false); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-5 ela-fade-in" data-testid="yaz-kampi-page">
      <div>
        <div className="flex items-center gap-2 text-amber-600 text-xs uppercase tracking-wider font-semibold">
          <Sun className="w-3.5 h-3.5" /> Yaz Tatili LGS Kampı
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mt-1">3 Aylık Yaz Programı</h1>
        <p className="text-sm text-slate-500 mt-1">Sürdürülebilir, motive edici, bunaltmayan plan.</p>
      </div>

      <Tabs value={month} onValueChange={(v) => { setMonth(v); load(v, minutes, vacation); }}>
        <TabsList className="bg-slate-100 rounded-xl grid grid-cols-3 w-full">
          {MONTHS.map((m) => (
            <TabsTrigger key={m.key} value={m.key} data-testid={`month-${m.key}`} className="data-[state=active]:bg-white">
              <m.icon className="w-3.5 h-3.5 mr-1.5" /> {m.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <Card className="p-5 rounded-2xl border-slate-200 mt-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-slate-700">Günlük süre: <span className="font-display font-bold">{minutes} dk</span></span>
            <div className="flex items-center gap-2">
              <Waves className="w-4 h-4 text-blue-500" />
              <span className="text-xs text-slate-600">Şile yazlık modu</span>
              <Switch checked={vacation} onCheckedChange={(v) => { setVacation(v); load(month, minutes, v); }}
                data-testid="vacation-switch" />
            </div>
          </div>
          <Slider value={[minutes]} min={30} max={120} step={15}
            onValueChange={(v) => setMinutes(v[0])}
            onValueCommit={(v) => load(month, v[0], vacation)}
            data-testid="summer-minutes-slider" />
          <div className="flex justify-between text-[10px] text-slate-400 mt-1">
            <span>30dk</span><span>60dk</span><span>90dk</span><span>120dk</span>
          </div>
        </Card>

        {MONTHS.map((m) => (
          <TabsContent key={m.key} value={m.key} className="space-y-3 mt-4">
            {loading && !plan && (
              <Card className="p-10 rounded-2xl text-center border-slate-200">
                <Loader2 className="w-8 h-8 text-amber-600 animate-spin mx-auto mb-2" />
                <p className="text-sm text-slate-500">AI koç {m.label} planını hazırlıyor…</p>
              </Card>
            )}
            {plan && month === m.key && <SummerPlanView plan={plan} loading={loading} />}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function SummerPlanView({ plan, loading }) {
  return (
    <div className={`space-y-3 ${loading ? "opacity-50" : ""}`} data-testid="summer-plan-view">
      <Card className="p-5 rounded-2xl border-amber-200 bg-amber-50/40">
        <div className="text-xs text-amber-700 uppercase tracking-wider font-semibold">{plan.ay_baslik}</div>
        <h2 className="font-heading text-lg sm:text-xl font-bold text-slate-900 mt-1">{plan.ay_temasi}</h2>
      </Card>

      <div className="space-y-3">
        {(plan.haftalik_yapi || []).map((h, i) => (
          <Card key={i} className="p-5 rounded-2xl border-slate-200" data-testid={`week-${h.hafta}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-8 h-8 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center font-display font-bold text-sm">
                {h.hafta}
              </span>
              <h3 className="font-heading font-semibold text-slate-900">{h.baslik}</h3>
            </div>
            <div className="flex gap-1.5 flex-wrap mb-2">
              {(h.odak_dersler || []).map((d, j) => (
                <span key={j} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{d}</span>
              ))}
            </div>
            <p className="text-sm text-slate-700">{h.gunluk_ornek}</p>
          </Card>
        ))}
      </div>

      {plan.kurallar?.length > 0 && (
        <Card className="p-5 rounded-2xl border-slate-200">
          <h3 className="font-heading font-semibold text-slate-900 mb-3">Altın Kurallar</h3>
          <ul className="space-y-2">
            {plan.kurallar.map((k, i) => (
              <li key={i} className="flex gap-2 text-sm text-slate-700">
                <span className="text-amber-600 font-bold">{i + 1}.</span> {k}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {plan.motivasyon && (
        <Card className="p-5 rounded-2xl bg-slate-900 text-white">
          <p className="text-sm">{plan.motivasyon}</p>
        </Card>
      )}
    </div>
  );
}
